import { Download } from "lucide-react"
import { QRCodeCanvas } from "qrcode.react"
import { useRef } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/**
 * OKS-690 — Download the invite QR as a print-ready PNG.
 *
 * Additive to the OKS-685 Show-QR dialog. Sits inside the dialog's
 * action column, below the copy fallback, as a `secondary` peer to the
 * copy button (never the dialog's primary emphasis).
 *
 * **Client-side PNG, zero new deps (TRD §2 Key Decision).** A dedicated
 * hidden `QRCodeCanvas` — from the SAME `qrcode.react` lib already used
 * for the visible `QRCodeSVG` preview — is rendered offscreen at high
 * resolution (1024px, 4-module quiet zone). On click we read
 * `canvas.toDataURL('image/png')` and trigger a programmatic
 * `<a download>` click. This is preferred over rasterizing the shipped
 * SVG: `toDataURL` is synchronous, the canvas is same-origin/untainted,
 * and the handler is trivially unit-testable by stubbing `toDataURL`.
 *
 * **Print resolution.** The on-screen preview stays 256px; this canvas
 * renders at 1024px with `marginSize={4}` so the code stays scannable
 * when enlarged on an A4 flyer/sign. `level="M"` + black-on-white match
 * the preview's encoded value exactly (single source of truth = `url`).
 *
 * **No backend surface.** The PNG encodes only `url` (`invite.url`),
 * already on the client and already public (the OKS-655 `/invite/<code>`
 * short link). No new route/controller/policy/Inertia prop. Brand
 * isolation is trivially preserved (identical surface to OKS-685).
 *
 * **No storage.** The download is a transient `data:` URI on a
 * programmatic anchor — no `localStorage`/`sessionStorage` (Crew rule).
 *
 * **No loading/error/toast state.** Generation is synchronous from a
 * known string; the visible short URL + copy button remain the
 * guaranteed fallback channel. The browser's native download UI owns
 * success feedback; the dialog stays open so the op can re-download.
 */

/**
 * Derive the downloaded filename from the invite URL.
 *
 * `InviteSummary` carries no `code` field (only `url`), so the code is
 * the last non-empty path segment of `invite.url` — after stripping any
 * `#hash`, `?query`, and trailing slash(es). Taken verbatim (no
 * case-change, no alphabet validation); for a well-formed short link
 * this is the 10-char Crockford short_code. Falls back to `invite.png`
 * when the parse yields nothing (defensive — the download still fires).
 *
 * @param url the public invite URL (`invite.url`)
 * @returns e.g. `invite-AB12CD34EF.png`, or `invite.png` on parse miss
 */
export function deriveInviteFilename(url: string): string {
  let path: string
  try {
    // Absolute URL: use the pathname so the host is never mistaken for a
    // code, and `?query`/`#hash` are dropped automatically.
    path = new URL(url).pathname
  } catch {
    // Relative/malformed: strip hash + query by hand.
    path = (url.split("#")[0] ?? "").split("?")[0] ?? ""
  }
  const trimmed = path.replace(/\/+$/, "")
  const segment = trimmed.split("/").pop() ?? ""
  return segment ? `invite-${segment}.png` : "invite.png"
}

interface InviteQrDownloadButtonProps {
  /** The public short invite URL to encode + download (`invite.url`). */
  url: string
  /** Override testid. Defaults to `invites-panel-qr-download-button`. */
  testId?: string
}

/** High-res PNG size (px). Preview stays 256px; this prints crisply. */
const DOWNLOAD_QR_SIZE = 1024
/** Quiet-zone modules around the code (print scan margin). */
const DOWNLOAD_QR_MARGIN = 4

export function InviteQrDownloadButton({
  url,
  testId = "invites-panel-qr-download-button",
}: InviteQrDownloadButtonProps) {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const handleDownload = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const dataUrl = canvas.toDataURL("image/png")
    const anchor = document.createElement("a")
    anchor.href = dataUrl
    anchor.download = deriveInviteFilename(url)
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  }

  return (
    <>
      {/* Hidden hi-res canvas — kept offscreen (still painted, so
          `toDataURL` reads a real buffer) and out of the a11y tree.
          NOT `display:none`: this positioning guarantees the canvas
          renders its module buffer for the download. */}
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute top-0 -left-[9999px] h-0 w-0 overflow-hidden",
        )}
      >
        <QRCodeCanvas
          ref={canvasRef}
          value={url}
          size={DOWNLOAD_QR_SIZE}
          level="M"
          marginSize={DOWNLOAD_QR_MARGIN}
          bgColor="#ffffff"
          fgColor="#000000"
          data-testid="invite-qr-download-canvas"
        />
      </div>

      <Button
        type="button"
        variant="secondary"
        onClick={handleDownload}
        data-testid={testId}
        className="w-full sm:w-auto"
      >
        <Download className="size-4" aria-hidden="true" />
        {t("invites.panel.qr.download")}
      </Button>
    </>
  )
}
